import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle, CheckCircle, FileUp, Loader2, X } from "lucide-react";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AxiosHttpClient } from "../../../../services/AxiosHttpService";
import {
  LEGAL_CATEGORIES,
  LEGAL_COUNTRIES,
  LEGAL_TYPES,
} from "../../../../models/legalDomainConstants";

interface AddLegalDocumentProps {
  popupActive: boolean;
  setPopupActive: (active: boolean) => void;
  onJobStarted?: (job: UploadJob) => void;
}

interface FileWithStatus {
  file: File;
  status: "pending" | "uploading" | "success" | "error";
  progress: number;
  errorMessage?: string;
}

interface UploadJob {
  id: string;
  status:
    | "PENDING"
    | "PROCESSING"
    | "COMPLETED"
    | "PARTIALLY_FAILED"
    | "FAILED";
  totalFiles: number;
  processedFiles: number;
  successFiles: number;
  failedFiles: number;
  tasks: UploadTask[];
}

interface UploadTask {
  id: string;
  fileName: string;
  originalFileName: string;
  status: "PENDING" | "PROCESSING" | "SUCCESS" | "FAILED";
  errorMessage?: string;
}

interface UploadMetadata {
  authors: string[];
  category: string;
  type: string;
  territoryIds: string[];
  tags: string[];
  version: string;
  validFrom: string;
  validUntil: string;
}

const emptyMetadata = (): UploadMetadata => ({
  authors: [],
  category: "",
  type: "",
  territoryIds: [],
  tags: [],
  version: "",
  validFrom: "",
  validUntil: "",
});

export function AddLegalDocument({
  popupActive,
  setPopupActive,
  onJobStarted,
}: AddLegalDocumentProps) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<FileWithStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [metadata, setMetadata] = useState<UploadMetadata>(emptyMetadata());
  const [authorInput, setAuthorInput] = useState("");
  const [tagInput, setTagInput] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;
    const newFiles: FileWithStatus[] = Array.from(selectedFiles).map(
      (file) => ({ file, status: "pending", progress: 0 })
    );
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const addAuthor = () => {
    const val = authorInput.trim();
    if (val && !metadata.authors.includes(val)) {
      setMetadata((prev) => ({ ...prev, authors: [...prev.authors, val] }));
    }
    setAuthorInput("");
  };

  const removeAuthor = (author: string) => {
    setMetadata((prev) => ({
      ...prev,
      authors: prev.authors.filter((a) => a !== author),
    }));
  };

  const addTag = () => {
    const val = tagInput.trim();
    if (val && !metadata.tags.includes(val)) {
      setMetadata((prev) => ({ ...prev, tags: [...prev.tags, val] }));
    }
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    setMetadata((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  };

  const toggleTerritory = (id: string) => {
    setMetadata((prev) => ({
      ...prev,
      territoryIds: prev.territoryIds.includes(id)
        ? prev.territoryIds.filter((t) => t !== id)
        : [...prev.territoryIds, id],
    }));
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;
    setIsUploading(true);

    try {
      const formData = new FormData();
      files.forEach((fileWithStatus) => {
        formData.append("files", fileWithStatus.file);
      });

      // Serialize metadata (only non-empty fields)
      const metaPayload: Partial<UploadMetadata> = {};
      if (metadata.authors.length) metaPayload.authors = metadata.authors;
      if (metadata.category) metaPayload.category = metadata.category;
      if (metadata.type) metaPayload.type = metadata.type;
      if (metadata.territoryIds.length)
        metaPayload.territoryIds = metadata.territoryIds;
      if (metadata.tags.length) metaPayload.tags = metadata.tags;
      if (metadata.version) metaPayload.version = metadata.version;
      if (metadata.validFrom) metaPayload.validFrom = metadata.validFrom;
      if (metadata.validUntil) metaPayload.validUntil = metadata.validUntil;

      if (Object.keys(metaPayload).length > 0) {
        formData.append(
          "metadata",
          new Blob([JSON.stringify(metaPayload)], { type: "application/json" })
        );
      }

      setFiles((prev) =>
        prev.map((f) => ({ ...f, status: "uploading", progress: 10 }))
      );

      const job = await AxiosHttpClient.post<UploadJob>(
        "api/v1/docuveritas/legal-documents/upload",
        formData
      );
      onJobStarted?.(job);
      setPopupActive(false);
      setFiles([]);
      setMetadata(emptyMetadata());
      setAuthorInput("");
      setTagInput("");
    } catch (error: any) {
      console.error("Error uploading files:", error);
      setFiles((prev) =>
        prev.map((f) => ({
          ...f,
          status: "error",
          progress: 0,
          errorMessage: error?.message || "Erreur lors de l'upload",
        }))
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setPopupActive(false);
      setFiles([]);
      setMetadata(emptyMetadata());
      setAuthorInput("");
      setTagInput("");
    }
  };

  const totalFiles = files.length;
  const successFiles = files.filter((f) => f.status === "success").length;
  const errorFiles = files.filter((f) => f.status === "error").length;
  const processedFiles = successFiles + errorFiles;
  const overallProgress =
    totalFiles > 0 ? Math.round((processedFiles / totalFiles) * 100) : 0;

  return (
    <Dialog open={popupActive} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Ajouter des documents juridiques</DialogTitle>
          <DialogDescription>
            Uploadez des documents PDF ou des archives ZIP contenant des PDF.
            Vous pouvez ajouter plusieurs fichiers en une seule fois.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 py-4 pr-1">
          {/* Zone de drop */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
              transition-colors duration-200
              ${
                isDragging
                  ? "border-primary bg-primary/10"
                  : "border-muted-foreground/25 hover:border-primary/50"
              }
            `}
          >
            <FileUp className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm font-medium mb-1">
              Cliquez ou glissez-déposez vos fichiers ici
            </p>
            <p className="text-xs text-muted-foreground">
              PDF ou ZIP acceptés • Plusieurs fichiers possibles
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.zip"
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
            />
          </div>

          {/* Liste des fichiers */}
          {files.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">
                Fichiers sélectionnés ({files.length})
              </p>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {files.map((fileWithStatus, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 border rounded-lg bg-background"
                  >
                    <div className="flex-shrink-0">
                      {fileWithStatus.status === "pending" && (
                        <FileUp className="w-5 h-5 text-muted-foreground" />
                      )}
                      {fileWithStatus.status === "uploading" && (
                        <Loader2 className="w-5 h-5 text-primary animate-spin" />
                      )}
                      {fileWithStatus.status === "success" && (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                      {fileWithStatus.status === "error" && (
                        <AlertCircle className="w-5 h-5 text-destructive" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {fileWithStatus.file.name}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(fileWithStatus.file.size)}
                        </p>
                        {fileWithStatus.status === "uploading" && (
                          <p className="text-xs text-primary">
                            {fileWithStatus.progress}%
                          </p>
                        )}
                        {fileWithStatus.status === "error" && (
                          <p className="text-xs text-destructive">
                            {fileWithStatus.errorMessage}
                          </p>
                        )}
                      </div>
                      {fileWithStatus.status === "uploading" && (
                        <Progress
                          value={fileWithStatus.progress}
                          className="h-1 mt-1"
                        />
                      )}
                    </div>

                    {fileWithStatus.status !== "uploading" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFile(index)}
                        className="flex-shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ============= METADATA FORM ============= */}
          <div className="border rounded-lg p-4 space-y-4 bg-muted/20">
            <p className="text-sm font-semibold text-foreground">
              Attributs communs aux documents
            </p>

            {/* Row: Category + Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Catégorie</Label>
                <Select
                  value={metadata.category}
                  onValueChange={(v) =>
                    setMetadata((prev) => ({ ...prev, category: v }))
                  }
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {LEGAL_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {t(c.labelKey, { defaultValue: c.value })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Type</Label>
                <Select
                  value={metadata.type}
                  onValueChange={(v) =>
                    setMetadata((prev) => ({ ...prev, type: v }))
                  }
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {LEGAL_TYPES.map((ty) => (
                      <SelectItem key={ty.value} value={ty.value}>
                        {t(ty.labelKey, { defaultValue: ty.value })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Auteurs */}
            <div className="space-y-1.5">
              <Label className="text-xs">Auteurs</Label>
              <div className="flex gap-2">
                <Input
                  className="h-9 text-sm"
                  placeholder="Ajouter un auteur..."
                  value={authorInput}
                  onChange={(e) => setAuthorInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addAuthor();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addAuthor}
                  className="h-9"
                >
                  Ajouter
                </Button>
              </div>
              {metadata.authors.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {metadata.authors.map((a) => (
                    <span
                      key={a}
                      className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full px-2.5 py-0.5"
                    >
                      {a}
                      <button
                        onClick={() => removeAuthor(a)}
                        className="hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="space-y-1.5">
              <Label className="text-xs">Tags</Label>
              <div className="flex gap-2">
                <Input
                  className="h-9 text-sm"
                  placeholder="Ajouter un tag..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addTag}
                  className="h-9"
                >
                  Ajouter
                </Button>
              </div>
              {metadata.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {metadata.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 text-xs bg-secondary text-secondary-foreground rounded-full px-2.5 py-0.5"
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Territoires */}
            <div className="space-y-1.5">
              <Label className="text-xs">Territoires</Label>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {LEGAL_COUNTRIES.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-1.5 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={metadata.territoryIds.includes(c.id)}
                      onChange={() => toggleTerritory(c.id)}
                      className="rounded border-muted-foreground"
                    />
                    {t(c.labelKey, { defaultValue: c.value })}
                  </label>
                ))}
              </div>
            </div>

            {/* Row: Version + Valid From + Valid Until */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Version</Label>
                <Input
                  className="h-9 text-sm"
                  placeholder="ex: 1.0"
                  value={metadata.version}
                  onChange={(e) =>
                    setMetadata((prev) => ({
                      ...prev,
                      version: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Valide du</Label>
                <Input
                  type="date"
                  className="h-9 text-sm"
                  value={metadata.validFrom}
                  onChange={(e) =>
                    setMetadata((prev) => ({
                      ...prev,
                      validFrom: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Valide au</Label>
                <Input
                  type="date"
                  className="h-9 text-sm"
                  value={metadata.validUntil}
                  onChange={(e) =>
                    setMetadata((prev) => ({
                      ...prev,
                      validUntil: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>
          {/* ============= END METADATA FORM ============= */}

          {/* Progression globale */}
          {isUploading && totalFiles > 0 && (
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Progression globale</span>
                <span className="font-medium">{overallProgress}%</span>
              </div>
              <Progress value={overallProgress} className="h-2" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>
                  {successFiles} réussi{successFiles > 1 ? "s" : ""}
                </span>
                {errorFiles > 0 && (
                  <span className="text-destructive">
                    {errorFiles} erreur{errorFiles > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>
          )}

        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
          >
            Annuler
          </Button>
          <Button
            onClick={uploadFiles}
            disabled={files.length === 0 || isUploading}
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Upload en cours...
              </>
            ) : (
              <>
                <FileUp className="w-4 h-4 mr-2" />
                Uploader {files.length > 0 ? `(${files.length})` : ""}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
